import type { DeliveryRecord, PublicationPlanRecord, VideoContentSummary, VideoPackageSummary } from '@youkuai/contracts';

import { deliveryDisplayState } from './delivery-workflow-state.ts';

export type ContentTableContentType = 'VIDEO' | 'ARTICLE';
export type ContentTableStatusGroup = 'PLANNING' | 'PRODUCING' | 'REVIEW' | 'DELIVERED';

/** 内容页表格的一行输入：既带内容本身，也带它在项目/服务包上下文里的可读名称。 */
export interface ContentTableEntry {
  content: VideoContentSummary;
  packageId: string;
  packageCode: string;
  packageLabel: string;
  contentType: ContentTableContentType;
  packageSummary?: VideoPackageSummary | undefined;
}

export interface ContentTableFacts {
  deliveriesByContentId: ReadonlyMap<string, ReadonlyArray<DeliveryRecord>>;
  publicationsByContentId: ReadonlyMap<string, PublicationPlanRecord>;
  canReadDeliveries: boolean;
  canReadPublications: boolean;
}

export interface ContentTablePublishPlatform {
  label: string;
  state: 'SUCCEEDED' | 'PLANNED' | 'FAILED';
}

export type ContentTablePublish =
  | { kind: 'none'; text: string }
  | { kind: 'customer'; text: string }
  | { kind: 'text'; text: string }
  | { kind: 'platforms'; platforms: ContentTablePublishPlatform[] };

export interface ContentTableRow {
  key: string;
  contentId: string;
  entry: ContentTableEntry;
  title: string;
  no: string;
  createdAt: string;
  type: ContentTableContentType;
  statusLabel: string;
  statusGroup: ContentTableStatusGroup;
  deliveryLabel: string;
  deliveryTone: 'green' | 'gold' | 'gray';
  deliveredOn: string | null;
  publish: ContentTablePublish;
  tierLabel: string;
  planner: string;
  editor: string;
  packageLabel: string;
  issues: number;
}

const STATUS_GROUP_LABEL: Record<ContentTableStatusGroup, string> = {
  PLANNING: '待策划', PRODUCING: '制作中', REVIEW: '待登记', DELIVERED: '已交付',
};
const PLANNER_TASK_CODE: Record<ContentTableContentType, string> = {
  VIDEO: 'VIDEO_PLANNING', ARTICLE: 'ARTICLE_WRITING',
};
const EDITOR_TASK_CODE: Record<ContentTableContentType, string> = {
  VIDEO: 'VIDEO_EDITING', ARTICLE: 'ARTICLE_DESIGN',
};
const PUBLICATION_STATUS_TEXT: Record<string, string> = {
  UNPLANNED: '待安排', PLANNED: '待发布', IN_PROGRESS: '发布中', COMPLETED: '已完成', VOID: '已作废',
};

export function contentTableStatusGroup(status: string): ContentTableStatusGroup {
  if (status === 'PLACEHOLDER' || status === 'PLANNED' || status === 'TIERED') return 'PLANNING';
  if (status === 'PENDING_DELIVERY') return 'REVIEW';
  if (status === 'DELIVERED') return 'DELIVERED';
  return 'PRODUCING';
}

export function contentTableStatusLabel(status: string): string {
  return STATUS_GROUP_LABEL[contentTableStatusGroup(status)];
}

/** 业务优先使用包内内容序号；迁移异常先回退历史编号，最后才显示系统代码。 */
export function contentTableNo(content: VideoContentSummary): string {
  return String(content.displayNo ?? content.legacyNo ?? content.contentCode);
}

export function contentTableTierLabel(entry: ContentTableEntry): string {
  const tier = entry.packageSummary?.tiers.find((item) => item.code === entry.content.tierCode);
  return tier?.name ?? (entry.content.tierCode ? '已选规格' : '未选档');
}

function taskPersonNames(content: VideoContentSummary, taskCode: string): string {
  const task = content.tasks.find((item) => item.taskCode === taskCode && item.status !== 'VOID');
  if (!task) return '未分派';
  const names = [...new Set(task.assignees.map((assignee) => assignee.displayName))];
  if (names.length) return names.join(' / ');
  return task.executorName ?? '未分派';
}

export function contentTablePlanner(entry: ContentTableEntry): string {
  return taskPersonNames(entry.content, PLANNER_TASK_CODE[entry.contentType]);
}

export function contentTableEditor(entry: ContentTableEntry): string {
  return taskPersonNames(entry.content, EDITOR_TASK_CODE[entry.contentType]);
}

function deliveryFor(entry: ContentTableEntry, facts: ContentTableFacts): Pick<ContentTableRow, 'deliveryLabel' | 'deliveryTone' | 'deliveredOn'> {
  const records = facts.deliveriesByContentId.get(entry.content.contentId) ?? [];
  if (!facts.canReadDeliveries) {
    const state = entry.content.status === 'DELIVERED' ? 'DELIVERED' :
      entry.content.status === 'PENDING_DELIVERY' ? 'PENDING_DELIVERY' : 'NOT_READY';
    return { deliveryLabel: state === 'DELIVERED' ? '已完成交付' : state === 'PENDING_DELIVERY' ? '待登记' : '未就绪',
      deliveryTone: state === 'DELIVERED' ? 'green' : state === 'PENDING_DELIVERY' ? 'gold' : 'gray', deliveredOn: null };
  }
  const state = deliveryDisplayState(entry.content.status, records);
  const current = records.find((record) => record.current);
  return {
    deliveryLabel: state === 'DELIVERED' ? '已完成交付' : state === 'PENDING_DELIVERY' ? '待登记' : '未就绪',
    deliveryTone: state === 'DELIVERED' ? 'green' : state === 'PENDING_DELIVERY' ? 'gold' : 'gray',
    deliveredOn: current?.actualDeliveredOn ?? null,
  };
}

function publishFor(entry: ContentTableEntry, facts: ContentTableFacts): ContentTablePublish {
  const plan = facts.canReadPublications ? facts.publicationsByContentId.get(entry.content.contentId) : undefined;
  if (plan) {
    if (plan.mode === 'CUSTOMER') return { kind: 'customer', text: '客户自行发布' };
    if (plan.platforms.length) {
      return { kind: 'platforms', platforms: plan.platforms.map((platform) => ({
        label: platform.platform, state: platform.status,
      })) };
    }
  }
  const task = entry.content.publicationTask;
  if (task) return { kind: 'text', text: PUBLICATION_STATUS_TEXT[task.status] ?? '待安排' };
  return { kind: 'none', text: '暂无发布计划' };
}

export function buildContentTableRows(entries: ReadonlyArray<ContentTableEntry>,
  facts: ContentTableFacts): ContentTableRow[] {
  return entries.map((entry) => {
    const delivery = deliveryFor(entry, facts);
    return {
      key: entry.content.contentId,
      contentId: entry.content.contentId,
      entry,
      title: entry.content.title ?? `待确定${entry.contentType === 'VIDEO' ? '视频' : '文章'}主题`,
      no: contentTableNo(entry.content),
      createdAt: entry.content.createdAt,
      type: entry.contentType,
      statusLabel: contentTableStatusLabel(entry.content.status),
      statusGroup: contentTableStatusGroup(entry.content.status),
      deliveryLabel: delivery.deliveryLabel,
      deliveryTone: delivery.deliveryTone,
      deliveredOn: delivery.deliveredOn,
      publish: publishFor(entry, facts),
      tierLabel: contentTableTierLabel(entry),
      planner: contentTablePlanner(entry),
      editor: contentTableEditor(entry),
      packageLabel: entry.packageLabel,
      issues: entry.content.issueStats.open,
    };
  });
}
